import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';

const faqCategories = [
  {
    name: 'Général',
    questions: [
      {
        q: "Qu'est-ce que Weblax ?",
        a: "Weblax est une agence digitale spécialisée dans la création de sites internet, le développement d'applications, le référencement SEO et l'accompagnement digital des entreprises.",
      },
      {
        q: 'Où êtes-vous situés ?',
        a: "Nous sommes basés à Paris, mais nous travaillons avec des clients partout en France et à l'international grâce à nos outils de collaboration à distance.",
      },
      {
        q: 'Quels sont vos horaires ?',
        a: "Nous sommes disponibles du lundi au vendredi, de 9h à 18h. Pour les urgences, nos clients bénéficiant d'un support premium ont accès à une assistance étendue.",
      },
    ],
  },
  {
    name: 'Services',
    questions: [
      {
        q: 'Quels types de sites créez-vous ?',
        a: 'Nous créons tous types de sites : sites vitrines, sites corporate, sites e-commerce, applications web, landing pages, blogs et plus encore.',
      },
      {
        q: 'Proposez-vous le référencement SEO ?',
        a: 'Oui, nous proposons des services complets de référencement naturel incluant audit SEO, optimisation technique, stratégie de contenu et netlinking.',
      },
      {
        q: 'Faites-vous la maintenance des sites ?',
        a: 'Oui, nous proposons des forfaits de maintenance incluant mises à jour, sauvegardes, monitoring et support technique.',
      },
      {
        q: 'Développez-vous des applications mobiles ?',
        a: 'Oui, nous développons des applications mobiles natives et cross-platform selon vos besoins et votre budget.',
      },
    ],
  },
  {
    name: 'Tarifs et paiement',
    questions: [
      {
        q: 'Comment sont calculés vos tarifs ?',
        a: 'Nos tarifs sont basés sur la complexité du projet, le nombre de pages ou fonctionnalités, et les services additionnels choisis. Chaque devis est personnalisé.',
      },
      {
        q: 'Proposez-vous des facilités de paiement ?',
        a: 'Oui, nous proposons généralement un paiement en 3 fois. Pour les projets plus importants, nous pouvons discuter de modalités de paiement adaptées à vos besoins.',
      },
      {
        q: 'Le devis est-il gratuit ?',
        a: "Oui, tous nos devis sont gratuits et sans engagement. Nous prenons le temps d'analyser votre projet avant de vous proposer une offre adaptée.",
      },
      {
        q: 'Y a-t-il des frais cachés ?',
        a: 'Non, nous pratiquons une totale transparence. Tous les coûts sont détaillés dans le devis : création, hébergement, maintenance, etc.',
      },
    ],
  },
  {
    name: 'Processus de travail',
    questions: [
      {
        q: 'Comment se déroule un projet ?',
        a: 'Notre processus comprend 4 étapes : 1) Découverte et analyse des besoins, 2) Conception et maquettage, 3) Développement et tests, 4) Livraison et mise en ligne.',
      },
      {
        q: 'Quels sont les délais de réalisation ?',
        a: 'Les délais varient selon le projet : 2-4 semaines pour un site vitrine, 4-8 semaines pour un e-commerce, et 2-6 mois pour une application complexe.',
      },
      {
        q: "Puis-je suivre l'avancement de mon projet ?",
        a: 'Oui, vous avez accès à un espace client dédié et nous organisons des points réguliers pour valider chaque étape.',
      },
      {
        q: 'Combien de révisions sont incluses ?',
        a: 'Le nombre de révisions est défini dans le devis. Généralement, 2-3 tours de révisions sont inclus pour les maquettes et le développement.',
      },
    ],
  },
  {
    name: 'Support et maintenance',
    questions: [
      {
        q: 'Que comprend la maintenance ?',
        a: 'Notre maintenance inclut : mises à jour de sécurité, sauvegardes régulières, monitoring 24/7, corrections de bugs et support par email.',
      },
      {
        q: 'Comment contacter le support ?',
        a: 'Vous pouvez nous contacter par email, téléphone ou via votre espace client. Les délais de réponse dépendent de votre formule de support.',
      },
      {
        q: 'Proposez-vous des formations ?',
        a: "Oui, nous proposons des formations à l'utilisation de votre site ou application, incluses ou en option selon les projets.",
      },
    ],
  },
];

export const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  const pageTitle =
    'FAQ Weblax | Questions fréquentes sur la création de site internet, SEO et services web';
  const pageDescription =
    'Consultez la FAQ Weblax : réponses sur la création de site internet, les tarifs, le SEO, la maintenance, les délais, le devis et le fonctionnement de notre agence web.';
  const pageUrl = 'https://weblax.fr/faq';
  const pageImage = 'https://weblax.fr/og-image.jpg';

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const filteredCategories = faqCategories
    .map((category) => ({
      ...category,
      questions: category.questions.filter(
        (q) =>
          q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.a.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((category) => category.questions.length > 0);

  const allQuestions = faqCategories.flatMap((category) => category.questions);

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allQuestions.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'FAQ Weblax',
    url: pageUrl,
    description: pageDescription,
  };

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Weblax',
    url: 'https://weblax.fr',
    logo: 'https://weblax.fr/logo.png',
    email: 'contact@weblax.fr',
    telephone: '+33685802016',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Paris',
      addressCountry: 'FR',
    },
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="FAQ agence web, questions fréquentes site internet, création site web, devis site internet, tarifs agence web, SEO, maintenance site web, développement web, Weblax"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
      </Helmet>

      <div data-testid="faq-page">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28 text-center">
              <p className="weblax-caption text-primary mb-4">FAQ</p>
              <h1 className="weblax-heading-display mb-6 max-w-3xl mx-auto">
                Questions <span className="weblax-gradient-text">fréquentes</span>
              </h1>
              <p className="weblax-body-lg max-w-2xl mx-auto mb-8">
                Retrouvez les réponses aux questions les plus courantes.
                Vous ne trouvez pas votre réponse ? Contactez-nous !
              </p>
              <div className="max-w-md mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Rechercher une question..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="faq-search"
                  className="weblax-input pl-12"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="max-w-3xl mx-auto space-y-8">
              {filteredCategories.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <h2 className="weblax-heading-2 mb-4">{category.name}</h2>
                  <div className="space-y-3">
                    {category.questions.map((item, questionIndex) => {
                      const isOpen = openItems[`${categoryIndex}-${questionIndex}`];
                      return (
                        <Card key={questionIndex} className="weblax-card overflow-hidden">
                          <button
                            onClick={() => toggleItem(categoryIndex, questionIndex)}
                            className="w-full p-6 text-left flex items-start justify-between gap-4"
                            data-testid={`faq-item-${categoryIndex}-${questionIndex}`}
                          >
                            <h3 className="font-semibold pr-4">{item.q}</h3>
                            {isOpen ? (
                              <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                            )}
                          </button>
                          {isOpen && (
                            <CardContent className="px-6 pb-6 pt-0">
                              <p className="text-muted-foreground">{item.a}</p>
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}

              {filteredCategories.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">
                    Aucune question ne correspond à votre recherche.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSearchQuery('')}
                    className="weblax-btn-secondary"
                  >
                    Effacer la recherche
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="weblax-heading-1 mb-4">
                Vous n'avez pas trouvé votre réponse ?
              </h2>
              <p className="weblax-body-lg mb-8">
                Notre équipe est là pour vous aider. Contactez-nous et nous vous répondrons rapidement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button className="weblax-btn-primary">
                    Nous contacter
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/devis">
                  <Button variant="outline" className="weblax-btn-secondary">
                    Demander un devis
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;